import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { List, Modal, notification, Typography, Table, Pagination, Button } from 'antd';
import { InfoCircleOutlined, CloseCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

import { CustomNotification, UploadFile } from 'common/components';

import * as endpoints from 'services/gr-eval-form/endpoints';
import { UPLOAD_MAX_SIZE } from 'static/Constants';
import { useDispatchReloadPage } from 'hooks/useReloadPage';


import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/product';
import { title } from '@uiw/react-md-editor/lib/cjs/commands';

const { Text, Paragraph } = Typography;

const BulkUploadGRDataModal = (props) => {
  const intl = useIntl();
  const reloadPage = useDispatchReloadPage();

  const queryClient = useQueryClient();
  const { visible, setVisible } = props;


  const [errorList, setErrorList] = useState([]);
  const [msg, setmsg] = useState('');

  const onCancel = () => {
    closeModal();
    setErrorList([]);
    setmsg('');
    reloadPage();
  };

  const closeModal = () => {
    setVisible(false);
  };

  const handleResponseUpload = (response) => {
    setErrorList([]);
    setmsg('');

    const apiResponse = response?.data;

    if (!apiResponse) {
      return;
    }

    if (!apiResponse.isSuccess) {
      notification.error({
        message:
          apiResponse?.message ||
          'Failed to upload. Please check your file and try again.',
      });
      return;
    }

    const message = apiResponse?.data?.message;
    const report = apiResponse?.data?.report;
    const errors = report?.errors || [];


    CustomNotification.success(
      message || 'File uploaded successfully.'
    );

    if (errors.length > 0) {
      setErrorList(errors);
      setmsg(message || 'File uploaded with some errors. Please review the error list.')
      return;
    }

    reloadPage();
    closeModal();
  };

  const exportErrorsToCsv = () => {
    if (!errorList?.length) return;

    const header = 'ProjectNumber,Reason\n';
    const rows = errorList
      .map(
        (e) =>
          `${e.projectNumber || ''},"${(e.reason || '').replace(/"/g, '""')}"`
      )
      .join('\n');

    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'BulkUploadErrors.csv');
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;


  const renderErrorList = () => {
    const pagedData = errorList.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
    const columns = [
      {
        title: '',
        key: 'index',
        width: 20,
        render: () => (<Text strong>
          <CloseCircleOutlined style={{ color: 'red' }} />
        </Text>)
      },
      {
        title: 'Project#',
        dataIndex: 'projectNumber',
        key: 'projectNumber',
        width: 150,
        render: (value) => (
          <Text strong type='danger'>
            {value}
          </Text>
        ),
      },
      {
        title: 'Failure Reason',
        dataIndex: 'reason',
        key: 'reason',
        render: (value) => (
          <Text>
            {value || 'Invalid data'}
          </Text>
        ),
      },
    ];

    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <Text strong type="danger">
            <InfoCircleOutlined /> {msg}
          </Text>


          <Button icon={<DownloadOutlined />} onClick={exportErrorsToCsv}>Export CSV</Button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '60vh' }}>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <Table
              rowKey={(record, index) =>
                `${record.projectNumber}-${index}`
              }
              columns={columns}
              dataSource={pagedData}
              pagination={false}
              size="small"
              bordered
              type="danger"
            />
          </div>
          <Pagination
            style={{ marginTop: 16, textAlign: 'right' }}
            current={currentPage}
            pageSize={pageSize}
            showSizeChanger={false}
            total={errorList.length}
            showTotal={(total) => `Total ${total} items`}
            showQuickJumper
            onChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      title="Bulk Upload Projects"
      onCancel={onCancel}
      footer={
        errorList?.length ? (
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={onCancel}
              className='ant-btn ant-btn-primary'
            >
              Close
            </button>
          </div>
        ) : null
      }
      width={780}
      destroyOnClose
      maskClosable={false}
    >
      {!errorList?.length && (
        <div style={{ height: 350, overflow: 'hidden' }}>
          <UploadFile
            uploadAPL
            manualUpload={true}
            isBase64={false}
            showMultiple={false}
            supportTypes={['xlsx', 'xls']}
            showXlsTemplate={false}
            apiUrl={endpoints.BULK_UPLOAD_GR_EVAL_FORM}
            showUploadSuccessMsg={false}
            showServerMessage={false}
            notShowMessage={true}
            getResponse={(response) => handleResponseUpload(response)}
            maxSize={UPLOAD_MAX_SIZE.GENERAL}
          />
        </div>
      )}
      {errorList?.length ? renderErrorList() : null}
    </Modal>
  );
};

export default BulkUploadGRDataModal;
